import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useCatalogQuery,
  useAuthSessionQuery,
  useGeneratePdfMutation,
  useLatestPriceMutation,
  useLoginMutation,
  useLogoutMutation,
  usePricelistGridsQuery,
  useSaveWorkspaceMutation,
  useWorkspaceQuery,
  useWorkspacesQuery
} from "./api/hooks";
import { LoginScreen } from "./components/auth/LoginScreen";
import { CatalogPanel } from "./components/catalog/CatalogPanel";
import { ProductGrid } from "./components/catalog/ProductGrid";
import { QuotationCart } from "./components/cart/QuotationCart";
import { DocumentSettings, PricingSettings } from "./components/document/DocumentSettings";
import { WorkspaceSidebar } from "./components/workspace/WorkspaceSidebar";
import {
  findBrand,
  findCategory,
  findGrid,
  findPricelist,
  pricelistSelectionKey
} from "./domain/catalog";
import { buildQuotationRequest } from "./domain/pricing";
import {
  cartItemGridKey,
  makeCartItemGridKey,
  makeCartItemId,
  type CartItem
} from "./domain/quotation";
import { deriveWorkspaceTitle, restoreWorkspaceState } from "./domain/workspace";
import { initialWorkspaceState, workspaceReducer } from "./state/workspaceReducer";

export function App() {
  const queryClient = useQueryClient();
  const [state, dispatch] = useReducer(workspaceReducer, initialWorkspaceState);
  const [recentlyAddedItemId, setRecentlyAddedItemId] = useState<string>();
  useEffect(() => {
    if (!recentlyAddedItemId) return;
    const timer = window.setTimeout(() => setRecentlyAddedItemId(undefined), 1800);
    return () => window.clearTimeout(timer);
  }, [recentlyAddedItemId]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeWorkspaceId, setActiveWorkspaceId] = useState<string | undefined>();
  const [workspaceTitle, setWorkspaceTitle] = useState("");
  const [workspaceIdToLoad, setWorkspaceIdToLoad] = useState<string | undefined>();
  const [lastPdfUrl, setLastPdfUrl] = useState<string | undefined>();
  const [lastPdfNumber, setLastPdfNumber] = useState<string | undefined>();
  const [restoreWarnings, setRestoreWarnings] = useState<string[]>([]);
  const authSessionQuery = useAuthSessionQuery();
  const isAuthenticated = authSessionQuery.data?.authenticated === true;
  const loginMutation = useLoginMutation();
  const logoutMutation = useLogoutMutation();
  const catalogQuery = useCatalogQuery(isAuthenticated);
  const gridsQuery = usePricelistGridsQuery(isAuthenticated);
  const workspacesQuery = useWorkspacesQuery(isAuthenticated);
  const workspaceQuery = useWorkspaceQuery(workspaceIdToLoad, isAuthenticated);
  const saveWorkspaceMutation = useSaveWorkspaceMutation();
  const pdfMutation = useGeneratePdfMutation();
  const latestPriceMutation = useLatestPriceMutation();
  const pendingComparisonLookups = useRef(new Set<string>());

  const selectedBrand = useMemo(
    () => findBrand(catalogQuery.data?.brands, state.selectedBrand),
    [catalogQuery.data?.brands, state.selectedBrand]
  );
  const selectedCategory = useMemo(
    () => findCategory(selectedBrand, state.selectedCategory),
    [selectedBrand, state.selectedCategory]
  );
  const selectedPricelist = useMemo(
    () => findPricelist(selectedCategory, state.selectedPricelistTag),
    [selectedCategory, state.selectedPricelistTag]
  );
  const selectedGrid = useMemo(
    () =>
      findGrid(
        gridsQuery.data,
        selectedBrand?.brand ?? "",
        selectedCategory?.category ?? "",
        selectedPricelist
      ),
    [gridsQuery.data, selectedBrand, selectedCategory, selectedPricelist]
  );
  const majorityBrand = useMemo(() => findMajorityBrand(state.cart), [state.cart]);
  const comparisonBrandOptions = useMemo(
    () =>
      (catalogQuery.data?.brands ?? [])
        .map((brand) => brand.brand)
        .filter((brand) => brand !== majorityBrand),
    [catalogQuery.data?.brands, majorityBrand]
  );
  const defaultComparisonBrand = useMemo(
    () => preferredComparisonBrand(majorityBrand, comparisonBrandOptions),
    [majorityBrand, comparisonBrandOptions]
  );
  const comparisonBrand = state.comparison.brand || defaultComparisonBrand;
  const comparisonCartIds = useMemo(
    () => state.cart.map((item) => item.id).join("|"),
    [state.cart]
  );
  const selectedGridItemKeys = useMemo(() => {
    if (!selectedGrid) {
      return new Set<string>();
    }

    return new Set(
      state.cart
        .filter(
          (item) =>
            item.brand === selectedGrid.brand &&
            item.category === selectedGrid.category &&
            item.pricelistDisplayName === selectedGrid.display_name
        )
        .map(cartItemGridKey)
    );
  }, [selectedGrid, state.cart]);

  useEffect(() => {
    if (!selectedBrand || !selectedCategory || !selectedPricelist) {
      return;
    }

    dispatch({
      type: "sync-selection",
      brand: selectedBrand.brand,
      category: selectedCategory.category,
      tag: pricelistSelectionKey(selectedPricelist)
    });
  }, [selectedBrand, selectedCategory, selectedPricelist]);

  useEffect(() => {
    if (!workspaceQuery.data || workspaceQuery.data.id !== workspaceIdToLoad) {
      return;
    }

    setRecentlyAddedItemId(undefined);
    const restored = restoreWorkspaceState(workspaceQuery.data.state);
    dispatch({ type: "replace-state", state: restored.state });
    setRestoreWarnings(restored.warnings);
    setActiveWorkspaceId(workspaceQuery.data.id);
    setWorkspaceTitle(workspaceQuery.data.title);
    setLastPdfUrl(workspaceQuery.data.last_pdf_url ?? undefined);
    setLastPdfNumber(workspaceQuery.data.last_pdf_number ?? undefined);
    setWorkspaceIdToLoad(undefined);
    pdfMutation.reset();
  }, [workspaceQuery.data, workspaceIdToLoad, pdfMutation]);

  useEffect(() => {
    const itemIds = state.cart.map((item) => item.id);
    dispatch({ type: "sync-comparison-items", itemIds });

    if (state.cart.length === 0 || !comparisonBrand) {
      return;
    }

    if (state.comparison.brand && !comparisonBrandOptions.includes(state.comparison.brand)) {
      if (defaultComparisonBrand) {
        dispatch({ type: "set-comparison-brand", brand: defaultComparisonBrand });
      }
      return;
    }

    if (!state.comparison.brand) {
      dispatch({ type: "set-comparison-brand", brand: comparisonBrand });
      return;
    }

    for (const item of state.cart) {
      const row = state.comparison.rows.find((comparison) => comparison.cartItemId === item.id);
      if (row?.listPrice !== undefined || row?.warning) {
        continue;
      }
      if (pendingComparisonLookups.current.has(comparisonLookupKey(comparisonBrand, item.id))) {
        continue;
      }

      void fetchComparisonListPrice(item, comparisonBrand);
    }
  }, [
    comparisonBrand,
    comparisonBrandOptions,
    comparisonCartIds,
    defaultComparisonBrand,
    state.comparison.brand,
    state.comparison.rows
  ]);

  async function handleSaveWorkspace(pdf?: { url: string; number: string }) {
    const title = workspaceTitle.trim() || deriveWorkspaceTitle(state);
    const savedWorkspace = await saveWorkspaceMutation.mutateAsync({
      id: activeWorkspaceId,
      title,
      state,
      lastPdfUrl: pdf?.url ?? lastPdfUrl,
      lastPdfNumber: pdf?.number ?? lastPdfNumber
    });

    setActiveWorkspaceId(savedWorkspace.id);
    setWorkspaceTitle(savedWorkspace.title);
    setLastPdfUrl(savedWorkspace.last_pdf_url ?? undefined);
    setLastPdfNumber(savedWorkspace.last_pdf_number ?? undefined);
    queryClient.setQueryData(
      ["web", "workspaces", savedWorkspace.id],
      savedWorkspace
    );
    await queryClient.invalidateQueries({
      queryKey: ["web", "workspaces"],
      exact: true
    });
  }

  function handleNewWorkspace() {
    dispatch({ type: "replace-state", state: initialWorkspaceState });
    setActiveWorkspaceId(undefined);
    setWorkspaceTitle("");
    setWorkspaceIdToLoad(undefined);
    setLastPdfUrl(undefined);
    setLastPdfNumber(undefined);
    setRestoreWarnings([]);
    pdfMutation.reset();
    saveWorkspaceMutation.reset();
  }

  async function handleGeneratePdf() {
    const request = buildQuotationRequest(state.cart, {
      deliveryCharges: state.deliveryCharges,
      customerLines: state.customerLines,
      showPriceBreakdown: state.showPriceBreakdown,
      termsText: state.termsText
    });

    const pdf = await pdfMutation.mutateAsync({ documentType: state.documentType, request });
    setLastPdfUrl(pdf.url);
    setLastPdfNumber(pdf.quotation_number);
    await handleSaveWorkspace({ url: pdf.url, number: pdf.quotation_number });
  }

  async function handleLogin(password: string) {
    await loginMutation.mutateAsync(password);
    await queryClient.invalidateQueries({ queryKey: ["web", "auth", "session"] });
  }

  async function handleLogout() {
    await logoutMutation.mutateAsync();
    handleNewWorkspace();
    queryClient.removeQueries({ queryKey: ["web"] });
    await queryClient.invalidateQueries({ queryKey: ["web", "auth", "session"] });
  }

  async function handleComparisonDiscountChange(item: CartItem, discount: number | undefined) {
    if (!state.comparison.brand && comparisonBrand) {
      dispatch({ type: "set-comparison-brand", brand: comparisonBrand });
    }
    dispatch({ type: "update-comparison-discount", id: item.id, discount });
  }

  async function fetchComparisonListPrice(item: CartItem, brand: string) {
    const lookupKey = comparisonLookupKey(brand, item.id);
    pendingComparisonLookups.current.add(lookupKey);
    try {
      const price = await latestPriceMutation.mutateAsync({
        brand,
        product: item.product,
        discount: 0
      });
      dispatch({
        type: "set-comparison-list-price",
        id: item.id,
        brand,
        listPrice: price.found ? (price.list_price ?? price.rate ?? undefined) : undefined,
        warning: price.found ? undefined : `Price not found for ${brand.toUpperCase()}`
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Price lookup failed";
      dispatch({
        type: "set-comparison-list-price",
        id: item.id,
        brand,
        listPrice: undefined,
        warning:
          message.includes("status 404")
            ? "Latest price lookup is not available. Please restart the backend server."
            : message
      });
    } finally {
      pendingComparisonLookups.current.delete(lookupKey);
    }
  }

  const isLoading = catalogQuery.isLoading || gridsQuery.isLoading;
  const loadError =
    catalogQuery.error instanceof Error
      ? catalogQuery.error.message
      : gridsQuery.error instanceof Error
        ? gridsQuery.error.message
        : undefined;
  const pdfError =
    pdfMutation.error instanceof Error ? pdfMutation.error.message : undefined;
  const saveError =
    saveWorkspaceMutation.error instanceof Error
      ? saveWorkspaceMutation.error.message
      : undefined;
  const workspaceLoadError =
    workspacesQuery.error instanceof Error
      ? workspacesQuery.error.message
      : workspaceQuery.error instanceof Error
        ? workspaceQuery.error.message
        : undefined;
  const loginError =
    loginMutation.error instanceof Error ? loginMutation.error.message : undefined;

  if (authSessionQuery.isLoading) {
    return <section className="status-panel auth-loading">Checking access...</section>;
  }

  if (!isAuthenticated) {
    return (
      <LoginScreen
        isLoading={loginMutation.isPending}
        errorMessage={loginError}
        onLogin={(password) => void handleLogin(password)}
      />
    );
  }

  return (
    <div className="app-frame">
      <WorkspaceSidebar
        collapsed={sidebarCollapsed}
        activeWorkspaceId={activeWorkspaceId}
        workspaces={workspacesQuery.data ?? []}
        loadError={workspaceLoadError}
        onToggleCollapsed={() => setSidebarCollapsed((collapsed) => !collapsed)}
        onNewWorkspace={handleNewWorkspace}
        onSelectWorkspace={(id) => setWorkspaceIdToLoad(id)}
      />

      <main className="app-shell">
        <header className="app-header">
          <div className="app-title-row">
            <h1>Quotation Workspace</h1>
            <input
              className="workspace-title-input"
              type="text"
              value={workspaceTitle}
              onChange={(event) => setWorkspaceTitle(event.currentTarget.value)}
              placeholder="Untitled Quote"
              aria-label="Workspace title"
            />
            <button
              type="button"
              className="secondary-button"
              disabled={logoutMutation.isPending}
              onClick={() => void handleLogout()}
            >
              {logoutMutation.isPending ? "Signing out" : "Logout"}
            </button>
          </div>
        </header>

        {isLoading && <section className="status-panel">Loading pricelists...</section>}
        {loadError && <section className="status-panel error-text">{loadError}</section>}

        {catalogQuery.data && (
          <CatalogPanel
            brands={catalogQuery.data.brands}
            selectedBrand={selectedBrand}
            selectedCategory={selectedCategory}
            selectedPricelist={selectedPricelist}
            onBrandChange={(brand) => dispatch({ type: "select-brand", brand })}
            onCategoryChange={(category) => dispatch({ type: "select-category", category })}
            onPricelistChange={(tag) => dispatch({ type: "select-pricelist", tag })}
            onQuickPricelistChange={(brand, category, tag) =>
              dispatch({ type: "sync-selection", brand, category, tag })
            }
          />
        )}

        <div className="workspace-grid">
          <div className="left-column">
            <ProductGrid
              grid={selectedGrid}
              selectedItemKeys={selectedGridItemKeys}
              onToggleCell={(cell) => {
                if (selectedGrid) {
                  const selectedItemGridKey = makeCartItemGridKey(cell);
                  const selectedItemId = makeCartItemId(cell, selectedGrid.tag);
                  const existingItem = state.cart.find(
                    (item) =>
                      item.id === selectedItemId ||
                      (item.brand === selectedGrid.brand &&
                        item.category === selectedGrid.category &&
                        item.pricelistDisplayName === selectedGrid.display_name &&
                        cartItemGridKey(item) === selectedItemGridKey)
                  );
                  setRecentlyAddedItemId(existingItem ? undefined : selectedItemId);
                  dispatch(
                    existingItem
                      ? { type: "remove-item", id: existingItem.id }
                      : { type: "add-cell", grid: selectedGrid, cell }
                  );
                }
              }}
            />
          </div>

          <aside className="right-column">
            {restoreWarnings.length > 0 && (
              <section className="panel restore-warning" aria-label="Workspace restore warning">
                {restoreWarnings.map((warning) => (
                  <p key={warning}>{warning}</p>
                ))}
              </section>
            )}

            <PricingSettings
              state={state}
              onDocumentTypeChange={(documentType) => dispatch({ type: "set-document-type", documentType })}
              onGlobalDiscountChange={(discount) => dispatch({ type: "set-global-discount", discount })}
              onGlobalMarkupChange={(markup) => dispatch({ type: "set-global-markup", markup })}
            />

            <QuotationCart
              onDeliveryChargesChange={(deliveryCharges) => dispatch({ type: "set-delivery-charges", deliveryCharges })}
              recentlyAddedItemId={recentlyAddedItemId}
              onPriceBreakdownChange={(enabled) => dispatch({ type: "set-price-breakdown", enabled })}
              state={state}
              comparisonBrand={comparisonBrand}
              comparisonBrandOptions={comparisonBrandOptions}
              isGenerating={pdfMutation.isPending}
              isSaving={saveWorkspaceMutation.isPending}
              result={pdfMutation.data}
              lastPdfUrl={lastPdfUrl}
              lastPdfNumber={lastPdfNumber}
              errorMessage={pdfError}
              saveErrorMessage={saveError}
              onQuantityChange={(id, quantity) =>
                dispatch({ type: "update-quantity", id, quantity })
              }
              onDiscountChange={(id, discount) =>
                dispatch({ type: "update-discount", id, discount })
              }
              onRateChange={(id, rate) => dispatch({ type: "update-rate", id, rate })}
              onMarkupChange={(id, markup) =>
                dispatch({ type: "update-markup", id, markup })
              }
              onFrlsChange={(id, enabled) =>
                dispatch({ type: "toggle-frls", id, enabled })
              }
              onPvcChange={(id, enabled) =>
                dispatch({ type: "toggle-pvc", id, enabled })
              }
              onComparisonBrandChange={(brand) =>
                dispatch({ type: "set-comparison-brand", brand })
              }
              onComparisonDiscountChange={(item, discount) =>
                void handleComparisonDiscountChange(item, discount)
              }
              onComparisonRateChange={(id, rate) =>
                {
                  if (!state.comparison.brand && comparisonBrand) {
                    dispatch({ type: "set-comparison-brand", brand: comparisonBrand });
                  }
                  dispatch({ type: "update-comparison-rate", id, rate });
                }
              }
              onRemove={(id) => dispatch({ type: "remove-item", id })}
              onSave={() => void handleSaveWorkspace()}
              onGenerate={() => void handleGeneratePdf()}
            >
              <DocumentSettings
                state={state}
                onCustomerLinesChange={(customerLines) => dispatch({ type: "set-customer-lines", customerLines })}
                onTermsTextChange={(termsText) => dispatch({ type: "set-terms-text", termsText })}
              />
            </QuotationCart>
          </aside>
        </div>
      </main>
    </div>
  );
}

function findMajorityBrand(items: CartItem[]): string {
  const counts = new Map<string, number>();
  for (const item of items) {
    counts.set(item.brand, (counts.get(item.brand) ?? 0) + 1);
  }

  return [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? "";
}

function preferredComparisonBrand(
  majorityBrand: string,
  options: string[]
): string {
  if (majorityBrand === "kei" && options.includes("polycab")) {
    return "polycab";
  }
  return options[0] ?? "";
}

function comparisonLookupKey(brand: string, itemId: string): string {
  return `${brand}::${itemId}`;
}
